// Title: Event Calendar Content
// Description: Active-view switchboard rendering month, week, day, N-days, or agenda; swappable per view via the components prop.

'use client';

import type { ComponentType, HTMLAttributes, ReactNode } from 'react';

import * as Slot from '@radix-ui/react-slot';

import { cn } from '../../lib/utils';
import { useEventCalendarSelector, useEventCalendarViewConfig } from './event-calendar';
import {
    EventCalendarDaysView,
    EventCalendarDayView,
    EventCalendarWeekView,
} from './event-calendar-time-grid';
import type { CalendarView } from './event-calendar-types';

const DEFAULT_VIEW_COMPONENTS: Partial<Record<CalendarView, ComponentType>> = {
    week: EventCalendarWeekView,
    day: EventCalendarDayView,
    days: EventCalendarDaysView,
};

interface EventCalendarContentProps extends HTMLAttributes<HTMLDivElement> {
    /** Swap individual view implementations. */
    components?: Partial<Record<CalendarView, ComponentType>>;
    /** Replaces the switchboard entirely; read useEventCalendarView() inside. */
    children?: ReactNode;
    asChild?: boolean;
}

function EventCalendarContent({
    className,
    asChild = false,
    components,
    children,
    ...props
}: EventCalendarContentProps) {
    const viewConfig = useEventCalendarViewConfig();
    const view = useEventCalendarSelector((state) => state.view);
    const loading = useEventCalendarSelector((state) => state.loading);

    const resolved = {
        ...DEFAULT_VIEW_COMPONENTS,
        ...viewConfig.components,
        ...components,
    };
    // A spread copies keys that hold `undefined`, so `components={{ month: isPro
    // ? ProMonth : undefined }}` would erase the default and render <undefined />.
    const ActiveView = resolved[view] ?? DEFAULT_VIEW_COMPONENTS[view];

    const Comp = asChild ? Slot.Root : 'div';

    return (
        <Comp
            data-slot="event-calendar-content"
            data-view={view}
            data-loading={loading || undefined}
            className={cn(
                'relative flex min-h-0 min-w-0 flex-1 flex-col',
                'data-loading:pointer-events-none data-loading:opacity-60',
                viewConfig.classNames?.content,
                className,
            )}
            {...props}
        >
            {children ?? (ActiveView ? <ActiveView /> : null)}
        </Comp>
    );
}

export type { EventCalendarContentProps };
export { DEFAULT_VIEW_COMPONENTS, EventCalendarContent };
